import c from 'classnames'
import { DateTime, Settings } from 'luxon'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import Loader from '../../../../../components/atoms/loader/Loader'
import Tag from '../../../../../components/atoms/tag/Tag'
import Text from '../../../../../components/atoms/text/Text'
import TouchableOpacity from '../../../../../components/atoms/touchableOpacity/TouchableOpacity'
import IconChevronRight from '../../../../../components/icons/IconChevronRight'
import routes, { generateRoute } from '../../../../../config/routes'
import { useAvailableSlots } from '../../../../../modules/meetingRoom/hooks'
import { IMeetingRoomSlot, IReservatedSeat } from '../../../../../types/app.types'
import { VARIANTS } from '../../../../../types/Components.types'
import styles from './HomeMeetingRoomAvailability.module.css'

Settings.defaultLocale = 'es'

interface IHomeMeetingRoomAvailability {
  reservation?: IReservatedSeat | null
}

export const HomeMeetingRoomAvailability: React.FC<IHomeMeetingRoomAvailability> = ({ reservation }) => {
  const { data: slots, isLoading: isLoadingSlots } = useAvailableSlots({
    date: reservation?.from,
    options: { enabled: !!reservation },
  })
  const navigate = useNavigate()

  if (!reservation) {
    return null
  }

  if (isLoadingSlots) {
    return (
      <section className={styles.container}>
        <Text size="l" variant={VARIANTS.primary} weight="700">
          Sala de reuniones
        </Text>

        <div className={styles.loadingContainer}>
          <Loader variant="primary" />
          <Text size="sm" variant={VARIANTS.dark} className={styles.loadingText}>
            Cargando disponibilidad...
          </Text>
        </div>
      </section>
    )
  }

  if (!slots || slots.length === 0) {
    return (
      <section className={styles.container}>
        <div className={styles.emptyContainer}>
          <Text size="l" variant={VARIANTS.primary} weight="700">
            Sala de reuniones
          </Text>
          <Text size="sm" variant={VARIANTS.dark}>
            No hay horarios disponibles para esta fecha
          </Text>
        </div>
      </section>
    )
  }

  const handleSeeAllSlotsClick = () => {
    navigate(generateRoute(routes.book.meetingRoom.availability, [{ name: 'bookId', value: reservation.seat.id }]))
  }

  const handleSlotClick = ({ slot }: { slot: IMeetingRoomSlot }) => {
    if (slot.status === 'free') {
      navigate(generateRoute(routes.book.meetingRoom.availability, [{ name: 'bookId', value: reservation.seat.id }]), {
        state: { slotHour: slot.hour },
      })
      return
    }

    if (!slot.reserve || !slot.reserve.id) {
      return
    }
    navigate(generateRoute(routes.workmates.publicProfile, [{ name: 'workmateId', value: slot.reserve.id }]), {
      state: { slotHour: slot.hour },
    })
  }

  const reservationDate = DateTime.fromISO(reservation.from)
  const dateTitle = reservationDate.toFormat("d 'de' LLLL")

  const freeSlots = slots.filter((slot) => slot.status === 'free').length
  const totalSlots = slots.length
  const occupancyPercentage = Math.round(((totalSlots - freeSlots) / totalSlots) * 100)

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <Text size="l" variant={VARIANTS.primary} weight="700">
            Sala de reuniones
          </Text>
          <Text size="sm" variant={VARIANTS.dark}>
            {dateTitle}
          </Text>
        </div>
        <div className={styles.statusSection}>
          <TouchableOpacity className={styles.chevronIcon} onClick={handleSeeAllSlotsClick}>
            <IconChevronRight className={styles.chevron} />
          </TouchableOpacity>
        </div>
      </div>

      <div className={styles.occupancySection}>
        <div className={styles.occupancyHeader}>
          <Text size="sm" variant={VARIANTS.dark}>
            Ocupación del día
          </Text>
          <Text size="sm" weight="600">
            {occupancyPercentage}%
          </Text>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${occupancyPercentage}%` }} />
        </div>

        <Tag variant={freeSlots > 0 ? 'primary' : 'negative'} textVariant={VARIANTS.white} className={styles.statusTag}>
          {freeSlots > 0 ? `${freeSlots} libres` : 'Completo'}
        </Tag>
      </div>

      <div>
        <Text size="sm" variant={VARIANTS.dark} className={styles.slotsTitle}>
          Horarios disponibles
        </Text>
        <div className={styles.slotsGrid}>
          {slots.map((slot) => {
            const slotTime = DateTime.fromISO(slot.hour)
            const timeLabel = slotTime.toFormat('HH:mm')
            const isAvailable = slot.status === 'free'

            return (
              <TouchableOpacity
                key={slot.id}
                className={c(styles.slotItem, isAvailable ? styles.slotAvailable : styles.slotOccupied)}
                onClick={() => handleSlotClick({ slot })}
              >
                <Text size="xs" weight="500" variant={isAvailable ? VARIANTS.primary : VARIANTS.light}>
                  {timeLabel}
                </Text>
              </TouchableOpacity>
            )
          })}
        </div>
      </div>
    </section>
  )
}
